import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { ArrowLeft, Save, Loader2, Upload, X } from "lucide-react";
import { notify } from "@/common/components/common/Notification";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Checkbox } from "@/common/components/ui/checkbox";
import Loader from "@/common/components/ui/loader";
import DatePickerInput from "@/common/components/features/date-picker-input";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import BannerService from "@/services/FinEaseHomeAndAdmin/APIs/BannerService";
import {
  BannerResponse,
  GetBannerDetailsByIdResponse,
} from "@/utils/types/Banner/Banner";

interface BannerPayload {
  title: string;
  startDate: string;
  endDate: string;
  enable: boolean;
  link: string;
  imageUrl: string;
}

const formatApiDateToPicker = (value: string) => {
  if (!value) {
    return "";
  }
  const [year, month, day] = value.split("-");
  if (year && month && day) {
    return `${day}/${month}/${year}`;
  }
  return value;
};

const formatPickerDateToApi = (value: string) => {
  if (!value) {
    return "";
  }
  const [day, month, year] = value.split("/");
  if (day && month && year) {
    return `${year}-${month}-${day}`;
  }
  return value;
};

const CreateBannerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<BannerPayload>({
    title: "",
    startDate: "",
    endDate: "",
    enable: true,
    link: "",
    imageUrl: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>("");

  const { data: bannerDetails, isLoading: isFetchingBanner } =
    useQuery<GetBannerDetailsByIdResponse>({
      queryKey: ["banner-detail", id],
      queryFn: () => BannerService.getBannerDetailsById(id ?? ""),
      enabled: Boolean(id),
    });

  const createBannerMutation = useMutation({
    mutationFn: (payload: FormData) => BannerService.createBanner(payload),
    onSuccess: (response: BannerResponse) => {
      if (response.success) {
        notify.success(response.message);
        queryClient.invalidateQueries({ queryKey: ["banners"] });
        navigate("/admin/cms/banners");
      } else {
        notify.error(response.error?.message);
      }
    },
    onError: (error: any) => {
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to save banner details"
      );
    },
  });

  const updateBannerMutation = useMutation({
    mutationFn: ({
      bannerId,
      payload,
    }: {
      bannerId: string;
      payload: FormData;
    }) => BannerService.updateBanner(bannerId, payload),
    onSuccess: (response: BannerResponse) => {
      if (response.success) {
        notify.success(response.message);
        queryClient.invalidateQueries({ queryKey: ["banners"] });
        navigate("/admin/cms/banners");
      } else {
        notify.error(response.error?.message);
      }
    },
    onError: (error: any) => {
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to update banner details"
      );
    },
  });

  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImagePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (formData.imageUrl) {
      setImagePreviewUrl(formData.imageUrl);
    } else {
      setImagePreviewUrl("");
    }
  }, [imageFile, formData.imageUrl]);

  useEffect(() => {
    const banner = bannerDetails?.data;
    if (!banner) {
      return;
    }

    const url = banner.banner_image_url ?? banner.banner_image ?? "";
    setFormData({
      title: banner.title ?? "",
      startDate: formatApiDateToPicker(banner.start_date),
      endDate: formatApiDateToPicker(banner.end_date),
      enable: Boolean(banner.is_enabled),
      link: banner.redirection_link ?? "",
      imageUrl: url,
    });
    setImagePreviewUrl(url);
    setImageFile(null);
  }, [bannerDetails]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange =
    (field: "startDate" | "endDate") => (value: string) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    };

  const buildFormData = () => {
    const payload = new FormData();
    payload.append("title", formData.title);
    payload.append("redirection_link", formData.link);
    payload.append("is_enabled", formData.enable ? "true" : "false");

    const startDate = formatPickerDateToApi(formData.startDate);
    const endDate = formatPickerDateToApi(formData.endDate);
    if (startDate) {
      payload.append("start_date", startDate);
    }
    if (endDate) {
      payload.append("end_date", endDate);
    }

    if (imageFile) {
      payload.append("banner_image", imageFile);
    } else if (formData.imageUrl) {
      payload.append("banner_image_url", formData.imageUrl);
    }

    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      return;
    }

    try {
      if (isEdit && id) {
        await updateBannerMutation.mutateAsync({
          bannerId: id,
          payload: buildFormData(),
        });
      } else {
        await createBannerMutation.mutateAsync(buildFormData());
      }
    } catch {
      // onError will show notification
    }
  };

  const showInitialLoader = (isEdit || isViewMode) && isFetchingBanner;
  const showSavingLoader =
    createBannerMutation.isPending || updateBannerMutation.isPending;

  if (showInitialLoader) {
    return <Loader message="Loading Banner Data..." />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 font-sans text-sm">
      {showSavingLoader && !showInitialLoader && (
        <Loader message={isEdit ? "Updating Banner..." : "Saving Banner..."} />
      )}
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-5xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() => navigate("/admin/cms/banners")}
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? `View Banner`
                      : isEdit
                        ? `Edit Banner`
                        : "Create New Banner"}
                  </h1>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure banner details, duration, and image
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate("/admin/cms/banners")}
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={showSavingLoader}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                  >
                    {showSavingLoader ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isEdit ? "Save Changes" : "Create Banner"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 overflow-auto bg-slate-50/50">
          <div className="mx-auto w-full max-w-5xl flex-1 p-6">
            <Card className="shadow-sm">
              <CardContent className="space-y-8 p-8">
                {/* Banner Details Section */}
                <div>
                  <h3 className="text-md mb-4 border-b pb-2 font-semibold text-primary">
                    Banner Information
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g. Diwali Offer"
                        disabled={isViewMode}
                        className="placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="link">Redirection Link</Label>
                      <Input
                        id="link"
                        name="link"
                        value={formData.link}
                        onChange={handleInputChange}
                        placeholder="https://"
                        disabled={isViewMode}
                        className="placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      />
                    </div>
                    <div className="flex flex-col justify-end space-y-2">
                      <div className="flex items-center space-x-2 py-2">
                        <Checkbox
                          id="enable"
                          checked={formData.enable}
                          onCheckedChange={checked =>
                            setFormData(prev => ({
                              ...prev,
                              enable: !!checked,
                            }))
                          }
                          disabled={isViewMode}
                        />
                        <Label
                          htmlFor="enable"
                          className="cursor-pointer font-medium"
                        >
                          Enable Banner?
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Duration Section */}
                <div>
                  <h3 className="text-md mb-4 border-b pb-2 font-semibold text-primary">
                    Duration
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Start Date</Label>
                      <DatePickerInput
                        value={formData.startDate}
                        onChange={handleDateChange("startDate")}
                        disabled={isViewMode}
                        placeholder="Select start date"
                        className="border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endDate">End Date</Label>
                      <DatePickerInput
                        value={formData.endDate}
                        onChange={handleDateChange("endDate")}
                        disabled={isViewMode}
                        placeholder="Select end date"
                        className="border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Media Section */}
                <div>
                  <h3 className="text-md mb-4 border-b pb-2 font-semibold text-primary">
                    Media Upload
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-4">
                      <Label>Banner Image</Label>
                      <div className="relative">
                        {!imagePreviewUrl ? (
                          <label className="flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 transition-colors hover:border-primary hover:bg-primary/5">
                            <Upload className="mb-2 h-8 w-8 text-slate-400" />
                            <span className="text-sm font-medium text-slate-500">
                              Click to upload banner image
                            </span>
                            <span className="mt-1 text-xs text-slate-400">
                              SVG, PNG, JPG or GIF
                            </span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={e =>
                                setImageFile(e.target.files?.[0] ?? null)
                              }
                              disabled={isViewMode}
                            />
                          </label>
                        ) : (
                          <div className="group relative h-40 w-full overflow-hidden rounded-lg border border-slate-200">
                            <img
                              src={imagePreviewUrl}
                              alt="Banner Preview"
                              className="h-full w-full object-cover transition-transform group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10" />
                            {!isViewMode && (
                              <Button
                                type="button"
                                size="icon"
                                variant="destructive"
                                className="absolute right-2 top-2 h-8 w-8 opacity-0 shadow-sm transition-all group-hover:opacity-100"
                                onClick={() => {
                                  setImageFile(null);
                                  setFormData({ ...formData, imageUrl: "" });
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateBannerPage;
